import { useCallback, useMemo, useState } from 'react';
import type { SortCountScanEventDTO, SortCountTargetDTO } from '../../types/sortAndCount';
import { buildDiffRows, buildSnapshot, buildSummary, makeScanEvent, validateBeforeSubmit } from './core';
import { buildStageSubmissionPayload } from './adapter';

type ApplyScanInput = {
  scanValue: string;
  qty?: number;
  source?: 'scan' | 'manual' | 'import';
  containerCode?: string;
};

const normalizeCode = (value: any): string => String(value || '').trim().toUpperCase();

export const useSortAndCountSession = (initial: {
  targets: SortCountTargetDTO[];
  events?: SortCountScanEventDTO[];
}) => {
  const [targets, setTargets] = useState<SortCountTargetDTO[]>(Array.isArray(initial?.targets) ? initial.targets : []);
  const [events, setEvents] = useState<SortCountScanEventDTO[]>(Array.isArray(initial?.events) ? initial.events : []);

  const targetCodeSet = useMemo(() => {
    return new Set((targets || []).map((target) => normalizeCode(target?.target_code)).filter(Boolean));
  }, [targets]);

  const rows = useMemo(() => buildDiffRows(targets, events), [targets, events]);
  const summary = useMemo(() => buildSummary(rows), [rows]);
  const snapshot = useMemo(() => buildSnapshot(targets, rows, events), [targets, rows, events]);
  const validation = useMemo(() => validateBeforeSubmit(rows), [rows]);

  const applyScan = useCallback((input: ApplyScanInput) => {
    const code = normalizeCode(input?.scanValue);
    if (!code) {
      return { ok: false, reason: 'empty_scan' };
    }
    if (!targetCodeSet.has(code)) {
      return { ok: false, reason: 'target_not_found' };
    }
    const event = makeScanEvent({
      scan_value: String(input?.scanValue || '').trim(),
      target_code: code,
      qty: Math.max(1, Number(input?.qty || 1)),
      source: input?.source || 'scan',
      container_code: input?.containerCode,
    });
    setEvents((prev) => [...prev, event]);
    return { ok: true, event };
  }, [targetCodeSet]);

  const removeEvent = useCallback((id: string) => {
    const safeId = String(id || '').trim();
    if (!safeId) return;
    setEvents((prev) => prev.filter((event) => String(event?.id || '') !== safeId));
  }, []);

  const clearEvents = useCallback(() => setEvents([]), []);

  const replaceTargets = useCallback((nextTargets: SortCountTargetDTO[]) => {
    setTargets(Array.isArray(nextTargets) ? nextTargets : []);
  }, []);

  const buildSubmission = useCallback((input?: {
    stageNo?: number | null;
    idempotencyKey?: string;
  }) => {
    return buildStageSubmissionPayload({
      stageNo: input?.stageNo,
      idempotencyKey: input?.idempotencyKey,
      targets,
      events,
    });
  }, [targets, events]);

  return {
    targets,
    events,
    rows,
    summary,
    snapshot,
    validation,
    setTargets: replaceTargets,
    setEvents,
    applyScan,
    removeEvent,
    clearEvents,
    buildSubmission,
  };
};

